// Header and UI Switcher Components for Gift-Wish - Premium Elegant Night
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/utils/api";
import { Gift, Globe, LogOut, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useLanguage, useAuth } from "@/contexts";
import { translations, languageNames } from "@/i18n";
import AuthModal from "@/AuthModal";
import GlobalSearch from "@/components/GlobalSearch";

// Language Switcher Component
export const LanguageSwitcher = () => {
  const { language, setLanguage } = useLanguage();
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button 
          className="w-10 h-10 flex items-center justify-center rounded-xl border border-border/50 bg-surface hover:bg-surface-2 hover:border-primary/30 transition-all"
          data-testid="language-switcher"
        >
          <Globe className="w-4 h-4 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-surface border-border/50">
        {Object.entries(languageNames).map(([code, { name, flag }]) => (
          <DropdownMenuItem
            key={code}
            onClick={() => setLanguage(code)}
            className={`cursor-pointer ${language === code ? "bg-primary/10 text-primary" : "hover:bg-surface-2"}`}
            data-testid={`lang-${code}`}
          >
            <span className="mr-2">{flag}</span>
            {name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Currency Switcher Component
export const CurrencySwitcher = () => {
  const { currency, setCurrency, availableCurrencies } = useLanguage();
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button 
          className="w-10 h-10 flex items-center justify-center rounded-xl border border-border/50 bg-surface hover:bg-surface-2 hover:border-primary/30 transition-all font-medium text-sm"
          data-testid="currency-switcher"
        >
          {currency}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-surface border-border/50">
        {availableCurrencies.map(({ code, symbol, name }) => (
          <DropdownMenuItem
            key={code}
            onClick={() => setCurrency(symbol)}
            className={`cursor-pointer ${currency === symbol ? "bg-primary/10 text-primary" : "hover:bg-surface-2"}`}
            data-testid={`currency-${code}`}
          >
            <span className="w-8 font-medium">{symbol}</span>
            <span className="text-muted-foreground">{name}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Header Component
export const Header = ({ showAuth = true, onLoginClick }) => {
  const { user, logout, setUser } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    if (user) {
      api.get(`/admin/check`)
        .then(res => setIsAdmin(res.data.is_admin))
        .catch(() => setIsAdmin(false));
    }
    return () => {
      if (!user) setIsAdmin(false);
    };
  }, [user]);

  const handleAuthSuccess = (userData) => {
    setUser(userData);
    navigate("/dashboard");
  };

  const authTranslations = translations[language] || translations.en;

  return (
    <>
      <header className="glass sticky top-0 z-50" data-testid="header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          {/* Logo - goes to dashboard if logged in, otherwise to landing */}
          <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-3 group" data-testid="logo-link">
            <div className="w-11 h-11 bg-gradient-to-br from-primary to-primary/70 rounded-xl flex items-center justify-center shadow-glow group-hover:shadow-glow-lg transition-all">
              <Gift className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-xl font-semibold tracking-tight">{t("app_name")}</span>
          </Link>

          {/* Actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            {user && <GlobalSearch />}
            <LanguageSwitcher />
            
            {showAuth && (
              <>
                {user ? (
                  <div className="flex items-center gap-2 sm:gap-3">
                    {isAdmin && (
                      <>
                        {/* Admin button - mobile icon */}
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => navigate("/admin")}
                          data-testid="admin-btn-mobile"
                          title={t("admin_panel")}
                          className="sm:hidden w-10 h-10 rounded-xl hover:bg-surface-2"
                        >
                          <Shield className="w-4 h-4" />
                        </Button>
                        {/* Admin button - desktop with text */}
                        <Button 
                          variant="ghost" 
                          onClick={() => navigate("/admin")}
                          data-testid="admin-btn"
                          className="hidden sm:flex h-10 rounded-xl hover:bg-surface-2"
                        >
                          Admin
                        </Button>
                      </>
                    )}
                    <div className="hidden md:flex items-center gap-3 pl-2 border-l border-border/30">
                      {user.picture && (
                        <img src={user.picture} alt="" className="w-9 h-9 rounded-xl border border-border/50" />
                      )}
                      <span className="text-sm font-medium text-muted-foreground">{user.name}</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={logout}
                      data-testid="logout-btn"
                      className="w-10 h-10 rounded-xl hover:bg-destructive/10 hover:text-destructive"
                    >
                      <LogOut className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button 
                    onClick={() => onLoginClick ? onLoginClick() : setShowAuthModal(true)}
                    className="premium-btn h-10 px-5 rounded-xl text-white text-sm font-medium"
                    data-testid="login-btn"
                  >
                    {t("login")}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </header>
    
      <AuthModal 
        open={showAuthModal} 
        onOpenChange={setShowAuthModal}
        onSuccess={handleAuthSuccess}
        translations={authTranslations}
        language={language}
      />
    </>
  );
};

export default Header;
